import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, MessageBar, MessageBarBody, Spinner, Text, makeStyles, tokens } from '@fluentui/react-components';
import { UploadControl } from './upload/UploadControl';
import { useUploadQueue, type UploadRules } from './upload/useUploadQueue';
import { ChartImportRefused, type ChartImportSummary, type ChartProblem } from '../services/conversionsApi';

const useStyles = makeStyles({
  box: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
  heading: { fontWeight: tokens.fontWeightSemibold },
  muted: { color: tokens.colorNeutralForeground3 },
  actions: { display: 'flex', gap: tokens.spacingHorizontalS, alignItems: 'center' },
  list: { margin: 0, paddingLeft: tokens.spacingHorizontalL },
});

/**
 * C87 for a chart file: ONE file, a CSV. Browsers name a CSV variously (Excel on Windows says `application/vnd.ms-excel`,
 * some say nothing), so the type check is the server's, which reads the file and refuses it with its rows (wave 5 §1.2).
 */
const RULES: UploadRules = { accept: ['*'], maxBytes: 2 * 1024 * 1024, maxFiles: 1 };

/** How many codes a result line lists before "…" (the full list is in the chart itself). */
const LISTED = 12;

export interface GlChartImportProps {
  /** Imports the file; resolves to what it did, rejects with `ChartImportRefused` when the file is refused whole. */
  onImport: (file: File) => Promise<ChartImportSummary>;
  /** The screen has unsaved edits: the import refreshes it, so it waits for Save. */
  dirty: boolean;
}

/**
 * "Import the chart from Xero" (wave 5 §1): Xero's Chart of Accounts CSV, through the shared upload control (C87). Merged
 * by code on the server, which never deletes and never un-archives. The result says what happened, by code; a refused file
 * lists its rows, each reason in the screen's language.
 */
export default function GlChartImport({ onImport, dirty }: GlChartImportProps) {
  const styles = useStyles();
  const { t } = useTranslation();
  const queue = useUploadQueue(RULES);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<{ file: string; summary: ChartImportSummary } | null>(null);
  const [refused, setRefused] = useState<{ problems: ChartProblem[]; count: number } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const codes = (list: string[]) => (list.length > LISTED ? `${list.slice(0, LISTED).join(', ')}, …` : list.join(', '));

  async function run() {
    setDone(null);
    setRefused(null);
    setError(null);
    setBusy(true);
    try {
      await queue.upload(async (file, _caption, onProgress) => {
        try {
          const summary = await onImport(file);
          onProgress(100);
          setDone({ file: file.name, summary });
        } catch (err) {
          if (err instanceof ChartImportRefused) setRefused({ problems: err.problems, count: err.problemCount });
          else setError(err instanceof Error ? err.message : t('common.unknownError'));
          throw err;
        }
      });
      queue.clear();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={styles.box}>
      <Text className={styles.heading}>{t('glMappingPage.chart.import.heading')}</Text>
      <Text className={styles.muted}>{t('glMappingPage.chart.import.hint')}</Text>
      <UploadControl queue={queue} captions={false} disabled={busy} testId="chart-upload-control" />
      <div className={styles.actions}>
        <Button
          appearance="primary"
          disabled={busy || dirty || queue.sendableCount === 0}
          title={dirty ? t('glMappingPage.chart.import.saveFirst') : undefined}
          onClick={() => void run()}
        >
          {busy ? t('glMappingPage.chart.import.importing') : t('glMappingPage.chart.import.button')}
        </Button>
        {busy && <Spinner size="tiny" />}
        {dirty && <Text className={styles.muted}>{t('glMappingPage.chart.import.saveFirst')}</Text>}
      </div>
      {done && (
        <MessageBar intent="success">
          <MessageBarBody>
            <div>
              {t('glMappingPage.chart.import.done', {
                file: done.file,
                added: done.summary.added.length,
                updated: done.summary.updated.length,
                unchanged: done.summary.unchanged.length,
              })}
            </div>
            {done.summary.notInFile.length > 0 && (
              <div>{t('glMappingPage.chart.import.notInFile', { codes: codes(done.summary.notInFile) })}</div>
            )}
            {done.summary.archivedKept.length > 0 && (
              <div>{t('glMappingPage.chart.import.archivedKept', { codes: codes(done.summary.archivedKept) })}</div>
            )}
          </MessageBarBody>
        </MessageBar>
      )}
      {refused && (
        <MessageBar intent="error">
          <MessageBarBody>
            <div>{t('glMappingPage.chart.import.refused')}</div>
            <ul className={styles.list}>
              {refused.problems.map((p, i) => (
                <li key={`${p.row}-${p.reason}-${i}`}>
                  {p.row > 0
                    ? t('glMappingPage.chart.import.row', { row: p.row, reason: t(`glMappingPage.chart.import.reasons.${p.reason}`, { value: p.value ?? '' }) })
                    : t(`glMappingPage.chart.import.reasons.${p.reason}`, { value: p.value ?? '' })}
                </li>
              ))}
            </ul>
            {refused.count > refused.problems.length && (
              <div>{t('glMappingPage.chart.import.moreProblems', { count: refused.count - refused.problems.length })}</div>
            )}
          </MessageBarBody>
        </MessageBar>
      )}
      {error && (
        <MessageBar intent="error">
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}
    </div>
  );
}
